import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import Header from '../components/Header';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Badge } from '../components/ui/badge';
import { 
  ArrowLeft, Crown, Mail, User, Calendar, LogOut
} from 'lucide-react';

export const ProfilePage = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const getInitials = (name) => {
    if (!name) return 'U';
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-white to-slate-50">
      <Header />
      
      <main className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Back Button */}
        <Button 
          variant="ghost" 
          className="mb-6"
          onClick={() => navigate('/home')}
          data-testid="back-btn"
        >
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back to Home
        </Button>

        <div className="mb-8">
          <h1 className="font-heading text-3xl md:text-4xl font-bold text-primary mb-2">
            Profile
          </h1>
          <p className="text-muted-foreground">
            Manage your account settings
          </p>
        </div>

        {/* Profile Card */}
        <Card className="border-slate-100 mb-6">
          <CardContent className="p-8">
            <div className="flex flex-col md:flex-row items-center gap-6">
              <Avatar className="h-24 w-24">
                <AvatarImage src={user?.picture} alt={user?.name} />
                <AvatarFallback className="bg-primary text-white text-2xl font-heading">
                  {getInitials(user?.name)}
                </AvatarFallback>
              </Avatar>
              
              <div className="text-center md:text-left flex-1">
                <div className="flex items-center justify-center md:justify-start gap-2 mb-2">
                  <h2 className="font-heading text-2xl font-bold text-primary">
                    {user?.name || 'User'}
                  </h2>
                  {user?.is_premium && (
                    <Badge className="bg-accent text-white">
                      <Crown className="h-3 w-3 mr-1" />
                      Premium
                    </Badge>
                  )}
                </div>
                <p className="text-muted-foreground">{user?.email}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Account Details */}
        <Card className="border-slate-100 mb-6">
          <CardHeader>
            <CardTitle className="font-heading">Account Details</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center gap-4 p-4 bg-slate-50 rounded-lg">
              <User className="h-5 w-5 text-muted-foreground" />
              <div>
                <p className="text-sm text-muted-foreground">Name</p>
                <p className="font-medium text-primary">{user?.name || 'Not set'}</p>
              </div>
            </div>
            <div className="flex items-center gap-4 p-4 bg-slate-50 rounded-lg">
              <Mail className="h-5 w-5 text-muted-foreground" />
              <div>
                <p className="text-sm text-muted-foreground">Email</p>
                <p className="font-medium text-primary">{user?.email}</p>
              </div>
            </div>
            <div className="flex items-center gap-4 p-4 bg-slate-50 rounded-lg">
              <Crown className="h-5 w-5 text-muted-foreground" />
              <div>
                <p className="text-sm text-muted-foreground">Plan</p>
                <p className="font-medium text-primary">
                  {user?.is_premium ? 'Premium' : 'Free'}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Upgrade Banner (for free users) */}
        {!user?.is_premium && (
          <Card className="border-0 bg-gradient-to-r from-primary to-secondary text-white mb-6">
            <CardContent className="p-6 flex flex-col md:flex-row items-center justify-between gap-4">
              <div>
                <h3 className="font-heading text-xl font-bold mb-1">
                  Upgrade to Premium
                </h3>
                <p className="text-white/80 text-sm">
                  Unlock all features, no watermarks, unlimited refinements
                </p>
              </div>
              <Button 
                className="bg-white text-primary hover:bg-white/90"
                onClick={() => navigate('/upgrade')}
                data-testid="upgrade-btn"
              >
                <Crown className="mr-2 h-4 w-4" />
                Upgrade Now
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Logout */}
        <Button 
          variant="outline" 
          className="w-full text-destructive border-destructive/20 hover:bg-destructive/5"
          onClick={handleLogout}
          data-testid="logout-btn"
        >
          <LogOut className="mr-2 h-4 w-4" />
          Sign Out
        </Button>
      </main>
    </div>
  );
};

export default ProfilePage;
